import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

type NavChild = { to: string; label: string };
type NavItem =
  | { label: string; children: NavChild[] }
  | { to: string; label: string; children?: undefined };

const navItems: NavItem[] = [
  {
    label: "認識學會",
    children: [
      { to: "/#philosophy", label: "創校理念" },
      { to: "/team", label: "專業團隊" },
      { to: "/#join", label: "加入我們" },
    ],
  },
  { to: "/treatments", label: "療程資訊" },
  { to: "/courses", label: "最新課程" },
  {
    label: "資訊中心",
    children: [
      { to: "/articles", label: "專業文章" },
      { to: "/faq", label: "常見問答" },
      { to: "/cases", label: "客人見證" },
    ],
  },
];

export default function Navbar() {
  const location = useLocation();
  const [scrollY, setScrollY] = useState(0);
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = location.pathname === "/";
  const solid = !isHome || scrollY > 40;

  const linkClass = (extra = "") =>
    `px-3 py-2 rounded-full text-sm border transition whitespace-nowrap cursor-pointer ${extra} ${
      solid
        ? "border-brand-200 text-brand-700 hover:bg-brand-50"
        : "border-white/70 text-white hover:bg-white/10"
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "bg-white/95 backdrop-blur shadow-[0_1px_0_rgba(0,0,0,0.06)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer -ml-1">
          <img
            src="https://storage.helloreaddy.io/project_files/5083fe3c-0446-4f05-ab30-834aed56ded7/6a623ee9-74ef-4c4d-9fa4-01d9417285e9_compressed_logo5.webp"
            alt="國際病變皮膚大健康及美業學會"
            className="h-9 md:h-11 w-auto object-contain"
          />
          <span
            className={`font-heading font-bold leading-none tracking-wide whitespace-nowrap text-sm md:text-base ${
              solid ? "text-brand-800" : "text-white"
            }`}
          >
            國際病變皮膚大健康及美業學會
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <button className={linkClass("flex items-center gap-1")}>
                  {item.label}
                  <i className="ri-arrow-down-s-line transition-transform group-hover:rotate-180"></i>
                </button>
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
                  <div className="bg-white border border-brand-100 rounded-lg py-1.5 min-w-[11rem]">
                    {item.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className="block px-4 py-2.5 text-sm text-brand-700 hover:bg-brand-50 hover:text-brand-900 whitespace-nowrap cursor-pointer"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={item.to} to={item.to} className={linkClass()}>
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <Link
              to="/member"
              className={`hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition ${
                solid ? "bg-brand-600 hover:bg-brand-700 text-white" : "bg-white text-brand-700 hover:bg-brand-50"
              }`}
            >
              <i className="ri-user-line"></i> 會員中心
            </Link>
          ) : (
            <Link
              to="/auth?redirect=/member"
              className={`hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition ${
                solid ? "bg-brand-600 hover:bg-brand-700 text-white" : "bg-white text-brand-700 hover:bg-brand-50"
              }`}
            >
              <i className="ri-user-line"></i> 登入 / 註冊
            </Link>
          )}
          <a
            href="https://recruit.ishb.hk"
            target="_blank"
            rel="nofollow noreferrer"
            className={`hidden md:inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition border ${
              solid ? "border-brand-300 text-brand-700 hover:bg-brand-50" : "border-white/70 text-white hover:bg-white/10"
            }`}
          >
            <i className="ri-user-add-line"></i> 報讀課程
          </a>
          <button
            onClick={() => setOpen(!open)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full border cursor-pointer ${
              solid ? "border-brand-300 text-brand-700" : "border-white/70 text-white"
            }`}
            aria-label="menu"
          >
            <i className={`ri-${open ? "close" : "menu"}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-brand-100 px-4 py-3 space-y-1">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label}>
                <button
                  onClick={() => setOpenGroup(openGroup === item.label ? null : item.label)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-md text-brand-800 hover:bg-brand-50 text-sm cursor-pointer"
                >
                  {item.label}
                  <i
                    className={`ri-arrow-down-s-line transition-transform ${
                      openGroup === item.label ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {openGroup === item.label && (
                  <div className="pl-3 border-l border-brand-100 ml-3 my-1 space-y-0.5">
                    {item.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        onClick={() => setOpen(false)}
                        className="block px-3 py-2 rounded-md text-brand-600 hover:bg-brand-50 text-sm cursor-pointer"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="block px-3 py-2 rounded-md text-brand-800 hover:bg-brand-50 text-sm cursor-pointer"
              >
                {item.label}
              </Link>
            )
          )}
          <a
            href="https://recruit.ishb.hk"
            target="_blank"
            rel="nofollow noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center justify-between px-3 py-2 rounded-md text-brand-800 hover:bg-brand-50 text-sm cursor-pointer"
          >
            報讀課程 <i className="ri-external-link-line text-xs"></i>
          </a>
          <Link
            to={user ? "/member" : "/auth?redirect=/member"}
            onClick={() => setOpen(false)}
            className="block px-3 py-2 rounded-md text-brand-800 hover:bg-brand-50 text-sm cursor-pointer"
          >
            {user ? "會員中心" : "登入 / 註冊"}
          </Link>
        </div>
      )}
    </header>
  );
}